import { useEffect, useState } from "react";
import { getLtik, getLtiKeyFromStorages, LTIK_LS_KEY, postGrade } from "../api";
import { jwtDecode } from "jwt-decode";
import { COMPLETED_CONTROL_SURVEY_ROUTE } from "../App";
import { useAppSelector } from "../app/store";

export const LTIK_SESSION_DATA_LS_KEY = "ltikSessionData";

export interface LtikSessionData {
    graded: boolean;
}

export type LtikSessionDataObject = Record<string, LtikSessionData>;

export function useWithLtikSession() {
    const [submittingGrade, setSubmittingGrade] = useState(false);
    const [gradePosted, setGradePosted] = useState(false);

    const user = useAppSelector((state) => state.user.data);
    const completedPostSurvey = user?.status.isPostTestSurveyComplete ?? false;

    useEffect(() => {
        const ltik = getLtiKeyFromStorages();
        if (!ltik) return;
        localStorage.setItem(LTIK_LS_KEY, ltik);

        try {
            const decodedLtik = jwtDecode(ltik);
            const userId = decodedLtik["user"];
            if (!userId) return;

            const isAtCompletedControlSurveyRoute = window.location.pathname.includes(COMPLETED_CONTROL_SURVEY_ROUTE);
            let allLtikSessionData: LtikSessionDataObject = localStorage.getItem(LTIK_SESSION_DATA_LS_KEY)
                ? JSON.parse(localStorage.getItem(LTIK_SESSION_DATA_LS_KEY))
                : {};

            if (!allLtikSessionData[userId]) {
                allLtikSessionData[userId] = {
                    graded: false,
                };
            }

            if ((completedPostSurvey && !allLtikSessionData[userId].graded) || isAtCompletedControlSurveyRoute) {
                setSubmittingGrade(true);
                postGrade(100)
                    .catch((error) => {
                        console.error("Error posting grade:", error);
                    })
                    .finally(() => {
                        allLtikSessionData[userId].graded = true;
                        updateAllLtikSessionData(allLtikSessionData);
                        setGradePosted(true);
                        setSubmittingGrade(false);
                    });
            } else {
                updateAllLtikSessionData(allLtikSessionData);
            }
        } catch (error) {
            console.error("Error decoding ltik:", error);
        }
    }, [completedPostSurvey]);

    function updateAllLtikSessionData(data: LtikSessionDataObject) {
        localStorage.setItem(LTIK_SESSION_DATA_LS_KEY, JSON.stringify(data));
    }

    function getCurrentLtikSessionData(): LtikSessionData | null {
        const ltik = getLtiKeyFromStorages();
        if (!ltik) return null;
        try {
            const decodedLtik = jwtDecode(ltik);
            const userId = decodedLtik["user"];
            if (!userId) return null;
            const allLtikSessionData: LtikSessionDataObject = localStorage.getItem(LTIK_SESSION_DATA_LS_KEY)
                ? JSON.parse(localStorage.getItem(LTIK_SESSION_DATA_LS_KEY))
                : {};
            return allLtikSessionData[userId];
        } catch (error) {
            console.error("Error decoding ltik:", error);
            return null;
        }
    }
    
    function getCanvasIdFromLtikToken(): string {
        try{
            const token = getLtik();
            const decodedToken = jwtDecode(token);
            return decodedToken["user"];
        }catch(error) {
            console.error("Error decoding ltik token:", error);
            return "";
        }
    }

    return {
        gradePosted,
        submittingGrade,
        isLtikSession: getCurrentLtikSessionData() ? true : false,
        canvasId: getCanvasIdFromLtikToken(),
    };
}
